
import { useState, useEffect } from 'react';
import { RealTimeAnalytics } from '@/types/assessment/analytics';
import { Industry } from '@/types/assessment';
import { useAuth } from '@/contexts/AuthContext';
import { fetchPreviousAssessment } from '@/services/assessmentHistoryService';
import { generateMetrics } from '@/services/analyticsMetricsService';
import { generateInsights } from '@/services/analyticsInsightsService';

interface UseRealTimeAnalyticsProps {
  dimensionScores: Record<string, number>;
  overallScore: number;
  industry: Industry;
  organizationName?: string;
}

export const useRealTimeAnalytics = ({ 
  dimensionScores, 
  overallScore,
  industry,
  organizationName = ''
}: UseRealTimeAnalyticsProps) => {
  const [analytics, setAnalytics] = useState<RealTimeAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previousAssessment, setPreviousAssessment] = useState<{
    scores: Record<string, number>,
    overallScore: number,
    date: string,
    organizationName: string
  } | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const getPreviousAssessment = async () => {
      if (!user?.email || !organizationName) return;
      
      const normalizedOrgName = organizationName.trim();
      console.log("Fetching previous assessment for organization:", normalizedOrgName);
      
      const prevAssessment = await fetchPreviousAssessment(user.email, normalizedOrgName);
      console.log("Previous assessment data:", prevAssessment);
      setPreviousAssessment(prevAssessment);
    };

    getPreviousAssessment();
  }, [user, organizationName]);

  const generateAnalytics = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const normalizedOrgName = organizationName.trim();
      const metrics = generateMetrics(dimensionScores, overallScore, industry, previousAssessment, normalizedOrgName);
      const insights = generateInsights(dimensionScores, industry);

      // We now pass the exact organization name when fetching the previous assessment,
      // so if we have a previousAssessment, it's already for the same organization
      const showComparison = !!previousAssessment;

      setAnalytics({
        metrics,
        insights,
        lastUpdated: new Date().toISOString(),
        comparisonInfo: showComparison ? {
          date: previousAssessment.date,
          formattedDate: new Date(previousAssessment.date).toLocaleDateString()
        } : undefined
      });
    } catch (err) {
      console.error('Error generating analytics:', err);
      setError('Failed to generate analytics. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    generateAnalytics();
  }, [dimensionScores, overallScore, industry, previousAssessment, organizationName]);

  const refreshAnalytics = () => {
    generateAnalytics();
  };

  return {
    analytics,
    isLoading,
    error,
    refreshAnalytics
  };
};
